import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import {
  TruckIcon,
  CheckCircleIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  BuildingStorefrontIcon,
  MapPinIcon,
  PhoneIcon,
  CalendarDaysIcon,
  DocumentArrowDownIcon,
  TagIcon,
  Cog6ToothIcon,
  BookOpenIcon,
  BanknotesIcon,
  PaperAirplaneIcon,
  QrCodeIcon
} from '@heroicons/react/24/outline';
import LogisticaConfigModal from '../components/LogisticaConfigModal';
import {
  obtenerEnviosWeb,
  actualizarEstadoEnvioWeb,
  generarEtiquetaEnvioWeb
} from '../api/backend_v2';

const MOCK_ENVIOS_DEMO = [
  {
    id: 'DEMO-1',
    es_demo: true,
    codigo_pedido: 'PED-94812',
    fecha: '2026-10-23T10:15:00',
    cliente_nombre: 'Mariana Gomez',
    cliente_email: 'mariana.gomez@gmail.com',
    cliente_telefono: '11 5492 8831',
    metodo_envio: 'envio_domicilio',
    operador_logistico: 'andreani',
    courier: 'Andreani Estándar a Domicilio',
    numero_seguimiento: 'AND-AR-8839104',
    direccion: 'Av. Santa Fe 3420, Piso 4 B, CABA, Buenos Aires (CP 1425)',
    items_resumen: '1x Vulk Rebel C1 Negro Mate + Estuche',
    metodo_pago: 'mercadopago',
    total: 185000,
    costo_envio: 8250,
    estado: 'en_transito'
  },
  {
    id: 'DEMO-2',
    es_demo: true,
    codigo_pedido: 'PED-94801',
    fecha: '2026-10-23T08:30:00',
    cliente_nombre: 'Esteban Falcone',
    cliente_email: 'esteban.falcone@hotmail.com',
    cliente_telefono: '11 4092 1120',
    metodo_envio: 'retiro_optica',
    operador_logistico: 'retiro_optica',
    courier: 'Retiro en Sucursal',
    numero_seguimiento: 'RET-OPT-94801',
    direccion: 'Retira en local central (Óptica)',
    items_resumen: '1x Rusty Danger Shiny Black',
    metodo_pago: 'transferencia',
    total: 142000,
    costo_envio: 0,
    estado: 'listo_retiro'
  }
];

export default function GestionEnvios({ onViewChange, usuario }) {
  const [envios, setEnvios] = useState([]);
  const [usandoDemo, setUsandoDemo] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroCourier, setFiltroCourier] = useState('todos');
  const [loading, setLoading] = useState(false);
  const [procesandoId, setProcesandoId] = useState(null);
  const [modalLogisticaOpen, setModalLogisticaOpen] = useState(false);
  const [modalInitialTab, setModalInitialTab] = useState('config');

  const cargarEnvios = useCallback(async () => {
    setLoading(true);
    try {
      const res = await obtenerEnviosWeb();
      if (res && Array.isArray(res.envios) && res.envios.length > 0) {
        setEnvios(res.envios);
        setUsandoDemo(false);
      } else {
        setEnvios(MOCK_ENVIOS_DEMO);
        setUsandoDemo(true);
      }
    } catch (err) {
      console.error('Error cargando envíos web:', err);
      if (envios.length === 0) {
        setEnvios(MOCK_ENVIOS_DEMO);
        setUsandoDemo(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarEnvios();
  }, [cargarEnvios]);

  const abrirModalLogistica = (tab = 'config') => {
    setModalInitialTab(tab);
    setModalLogisticaOpen(true);
  };

  const stats = {
    total: envios.length,
    pendientes_pago: envios.filter((e) => e.estado === 'pendiente_transferencia' || e.estado === 'pendiente').length,
    por_despachar: envios.filter((e) => e.estado === 'pagado' || e.estado === 'pendiente_despacho').length,
    en_transito: envios.filter((e) => e.estado === 'en_transito' || e.estado === 'listo_retiro').length,
    entregados: envios.filter((e) => e.estado === 'entregado').length
  };

  const enviosFiltrados = envios.filter((envio) => {
    const texto = busqueda.toLowerCase();
    const coincideTexto =
      (envio.cliente_nombre || '').toLowerCase().includes(texto) ||
      (envio.codigo_pedido || '').toLowerCase().includes(texto) ||
      (envio.numero_seguimiento || '').toLowerCase().includes(texto) ||
      (envio.direccion || '').toLowerCase().includes(texto) ||
      (envio.items_resumen || '').toLowerCase().includes(texto);

    const coincideEstado =
      filtroEstado === 'todos' ||
      envio.estado === filtroEstado ||
      (filtroEstado === 'por_despachar' && (envio.estado === 'pagado' || envio.estado === 'pendiente_despacho'));

    const op = (envio.operador_logistico || '').toLowerCase();
    const courierLower = (envio.courier || '').toLowerCase();
    const coincideCourier =
      filtroCourier === 'todos' ||
      (filtroCourier === 'andreani' && (op.includes('andreani') || courierLower.includes('andreani'))) ||
      (filtroCourier === 'envia' && (op.includes('envia') || courierLower.includes('correo') || courierLower.includes('oca') || courierLower.includes('urbano'))) ||
      (filtroCourier === 'retiro' && (envio.metodo_envio === 'retiro_optica' || op === 'retiro_optica'));

    return coincideTexto && coincideEstado && coincideCourier;
  });

  const cambiarEstado = async (envio, nuevoEstado) => {
    if (envio.es_demo) {
      setEnvios((prev) =>
        prev.map((e) => (e.id === envio.id ? { ...e, estado: nuevoEstado } : e))
      );
      Swal.fire({
        icon: 'success',
        title: 'Estado actualizado (Modo Demo)',
        timer: 1200,
        showConfirmButton: false
      });
      return;
    }

    setProcesandoId(envio.id);
    try {
      await actualizarEstadoEnvioWeb(envio.id, { estado: nuevoEstado });
      setEnvios((prev) =>
        prev.map((e) => (e.id === envio.id ? { ...e, estado: nuevoEstado } : e))
      );
      Swal.fire({
        icon: 'success',
        title: nuevoEstado === 'pagado' ? 'Pago acreditado y stock descontado' : 'Estado actualizado',
        text: `El pedido ${envio.codigo_pedido} pasó a '${nuevoEstado.replace('_', ' ')}'`,
        timer: 1600,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'No se pudo actualizar',
        text: error.message
      });
    } finally {
      setProcesandoId(null);
    }
  };

  const handleEmitirEtiqueta = async (envio) => {
    const { value: trackingInput, isConfirmed } = await Swal.fire({
      title: `Despachar Pedido ${envio.codigo_pedido}`,
      html: `
        <div style="text-align: left; font-size: 13px; color: #334155;">
          <p style="margin-bottom: 8px;"><strong>Operador elegido:</strong> ${envio.courier || 'Correo'}</p>
          <p style="margin-bottom: 12px;"><strong>Destino:</strong> ${envio.direccion}</p>
          <p style="font-size: 12px; color: #64748b; margin-bottom: 8px;">
            Dejá el campo vacío para <strong>generar la etiqueta automáticamente por API</strong> (Envia.com / Andreani), o escribí el código de seguimiento manualmente si despachaste en sucursal:
          </p>
        </div>
      `,
      input: 'text',
      inputValue: envio.numero_seguimiento || '',
      inputPlaceholder: 'Dejar vacío para autogenerar por API (ej. AND-AR-...)',
      showCancelButton: true,
      confirmButtonText: 'Generar Etiqueta y Despachar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0891b2'
    });

    if (!isConfirmed) return;

    if (envio.es_demo) {
      const trk = trackingInput || `AND-AR-${Math.floor(1000000 + Math.random() * 9000000)}`;
      setEnvios((prev) =>
        prev.map((e) => (e.id === envio.id ? { ...e, numero_seguimiento: trk, estado: 'en_transito' } : e))
      );
      Swal.fire({
        icon: 'success',
        title: 'Etiqueta generada (Demo)',
        text: `Tracking asignado: ${trk}`,
        timer: 1800,
        showConfirmButton: false
      });
      return;
    }

    setProcesandoId(envio.id);
    try {
      const res = await generarEtiquetaEnvioWeb(envio.id, {
        tracking_number: trackingInput ? trackingInput.trim() : ''
      });
      setEnvios((prev) =>
        prev.map((e) =>
          e.id === envio.id
            ? {
                ...e,
                numero_seguimiento: res.tracking_number,
                tracking_url: res.tracking_url,
                etiqueta_url: res.etiqueta_url,
                estado: res.estado
              }
            : e
        )
      );
      Swal.fire({
        icon: 'success',
        title: '¡Envío Despachado!',
        html: `
          <div style="font-size: 13px;">
            <p>Se asignó el código de seguimiento:</p>
            <p style="margin-top: 8px;"><code style="background: #f1f5f9; padding: 4px 10px; border-radius: 6px; font-weight: bold; font-size: 14px;">${res.tracking_number}</code></p>
          </div>
        `,
        confirmButtonText: 'Aceptar'
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error generando etiqueta',
        text: error.message
      });
    } finally {
      setProcesandoId(null);
    }
  };

  const imprimirRotulo = (envio) => {
    if (envio.etiqueta_url) {
      window.open(envio.etiqueta_url, '_blank', 'noopener,noreferrer');
      return;
    }

    const ventana = window.open('', '_blank', 'width=650,height=750');
    if (!ventana) {
      Swal.fire({
        icon: 'warning',
        title: 'Ventana emergente bloqueada',
        text: 'Permití las ventanas emergentes para imprimir el rótulo de envío.'
      });
      return;
    }

    const codigoTracking = envio.numero_seguimiento || `PEND-${envio.codigo_pedido}`;
    ventana.document.write(`
      <html>
        <head>
          <title>Rótulo de Envío - ${envio.codigo_pedido}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #0f172a; }
            .label-box { border: 3px solid #0f172a; border-radius: 12px; padding: 20px; max-width: 520px; margin: 0 auto; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #0f172a; padding-bottom: 12px; margin-bottom: 16px; }
            .carrier { font-size: 20px; font-weight: 900; text-transform: uppercase; }
            .order-code { font-size: 14px; font-weight: bold; background: #e2e8f0; padding: 4px 10px; border-radius: 6px; }
            .section-title { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #64748b; font-weight: bold; margin-bottom: 4px; }
            .dest-name { font-size: 18px; font-weight: bold; margin-bottom: 4px; }
            .dest-addr { font-size: 15px; line-height: 1.4; margin-bottom: 12px; }
            .cp-badge { display: inline-block; font-size: 18px; font-weight: 900; border: 2px solid #0f172a; padding: 4px 12px; border-radius: 6px; margin-bottom: 16px; }
            .tracking-box { text-align: center; border-top: 2px dashed #94a3b8; border-bottom: 2px dashed #94a3b8; padding: 16px 0; margin: 16px 0; }
            .barcode-bars { font-family: monospace; font-size: 28px; letter-spacing: 3px; font-weight: 900; }
            .tracking-num { font-family: monospace; font-size: 16px; font-weight: bold; margin-top: 6px; }
            .items { font-size: 12px; color: #334155; }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body>
          <div class="label-box">
            <div class="header">
              <div class="carrier">${envio.courier || 'ENVÍO ÓPTICA'}</div>
              <div class="order-code">${envio.codigo_pedido}</div>
            </div>
            <div class="section-title">Destinatario</div>
            <div class="dest-name">${envio.cliente_nombre}</div>
            <div class="dest-addr">${envio.direccion}</div>
            ${envio.codigo_postal ? `<div class="cp-badge">CP: ${envio.codigo_postal}</div>` : ''}
            <div style="font-size: 13px; margin-bottom: 10px;"><strong>Tel:</strong> ${envio.cliente_telefono || '-'}</div>
            <div class="tracking-box">
              <div class="barcode-bars">||| |||| || ||| |||| ||| ||</div>
              <div class="tracking-num">${codigoTracking}</div>
            </div>
            <div class="section-title">Contenido del Paquete (Frágil - Producto Óptico)</div>
            <div class="items">${envio.items_resumen || '1x Armazón Óptico'}</div>
          </div>
          <div class="no-print" style="text-align: center; margin-top: 20px;">
            <button onclick="window.print()" style="padding: 10px 20px; font-size: 14px; font-weight: bold; background: #0891b2; color: white; border: none; border-radius: 8px; cursor: pointer;">
              Imprimir Etiqueta Ahora
            </button>
          </div>
        </body>
      </html>
    `);
    ventana.document.close();
  };

  const notificarWhatsApp = (envio) => {
    const rawPhone = (envio.cliente_telefono || '').replace(/\D/g, '');
    if (!rawPhone) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin teléfono registrado',
        text: 'El cliente no ingresó un número de WhatsApp válido.'
      });
      return;
    }
    const phoneAr = rawPhone.startsWith('54') ? rawPhone : `549${rawPhone}`;
    const texto = encodeURIComponent(
      `¡Hola ${envio.cliente_nombre}! Te escribimos de la óptica por tu pedido *${envio.codigo_pedido}*.\n\n` +
      (envio.numero_seguimiento
        ? `Tu paquete ya fue despachado por *${envio.courier}*.\nCódigo de seguimiento: *${envio.numero_seguimiento}*\n`
        : `Estamos preparando tu pedido para enviarlo a *${envio.direccion}*.\n`) +
      `\n¡Cualquier consulta estamos a tu disposición!`
    );
    window.open(`https://wa.me/${phoneAr}?text=${texto}`, '_blank', 'noopener,noreferrer');
  };

  const getBadgeEstado = (estado) => {
    switch (estado) {
      case 'pendiente_transferencia':
      case 'pendiente':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-800 border border-rose-200">
            <BanknotesIcon className="w-3.5 h-3.5" /> Verificar Pago
          </span>
        );
      case 'pagado':
      case 'pendiente_despacho':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
            <ClockIcon className="w-3.5 h-3.5" /> Por Despachar
          </span>
        );
      case 'en_transito':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-sky-100 text-sky-800 border border-sky-200">
            <TruckIcon className="w-3.5 h-3.5" /> En Tránsito
          </span>
        );
      case 'listo_retiro':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200">
            <BuildingStorefrontIcon className="w-3.5 h-3.5" /> Para Retirar en Óptica
          </span>
        );
      case 'entregado':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
            <CheckCircleIcon className="w-3.5 h-3.5" /> Entregado
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
            {estado}
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-cyan-600 text-white shadow-md shadow-cyan-600/30">
              <TruckIcon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                Gestión de Envíos y Logística Web
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Despacho de pedidos online, generación de etiquetas y cotizador multi-correo (Andreani y Envia.com)
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            onClick={() => abrirModalLogistica('instructivo')}
            className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-amber-900 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-xl shadow-sm transition-all cursor-pointer"
            title="Ver guía paso a paso para dar de alta las cuentas y credenciales en Envia.com y Andreani"
          >
            <BookOpenIcon className="w-4 h-4 text-amber-700" />
            <span>Instructivo Paso a Paso (Alta de Credenciales)</span>
          </button>

          <button
            type="button"
            onClick={() => abrirModalLogistica('config')}
            className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-500 rounded-xl shadow-md shadow-indigo-600/20 transition-all hover:scale-105 active:scale-95 cursor-pointer"
            title="Configurar credenciales y tarifas de Andreani y Envia.com (Correo Argentino, OCA, Urbano)"
          >
            <Cog6ToothIcon className="w-4 h-4 text-indigo-100" />
            <span>Configurar Logística (Andreani / Envia.com)</span>
          </button>

          <button
            onClick={cargarEnvios}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 transition-colors shadow-sm cursor-pointer"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* Banner informativo rápido para el negocio */}
      <div className="mb-6 bg-gradient-to-r from-indigo-50 via-cyan-50 to-emerald-50 dark:from-slate-800 dark:to-slate-800 border border-indigo-200/80 dark:border-slate-700 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="text-xs text-slate-700 dark:text-slate-200 space-y-0.5">
          <p className="font-bold text-indigo-950 dark:text-white flex items-center gap-1.5">
            <span>¿Cómo activar las cotizaciones reales de Correo Argentino, OCA y Andreani?</span>
            {usandoDemo && (
              <span className="px-2 py-0.5 text-[10px] rounded-full bg-amber-200 text-amber-900 font-extrabold">
                Mostrando Pedidos de Ejemplo
              </span>
            )}
          </p>
          <p className="text-slate-600 dark:text-slate-300">
            Tus clientes ya pueden elegir en el Checkout entre el <strong>Mejor Precio Económico</strong> o <strong>Mayor Confianza</strong>. Para conectar tus cuentas oficiales del correo y emitir etiquetas reales, seguí nuestro instructivo guiado.
          </p>
        </div>
        <button
          type="button"
          onClick={() => abrirModalLogistica('instructivo')}
          className="shrink-0 px-3.5 py-2 rounded-xl bg-white dark:bg-slate-900 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-slate-600 text-xs font-bold hover:bg-indigo-50 transition-colors cursor-pointer"
        >
          Abrir Guía de Alta →
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700/60 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-1">
            Total Pedidos Web
          </span>
          <span className="text-2xl font-black text-slate-900 dark:text-white">{stats.total}</span>
        </div>

        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-rose-200/80 dark:border-rose-900/40 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400 block mb-1">
            Verificar Pago
          </span>
          <span className="text-2xl font-black text-rose-600 dark:text-rose-400">
            {stats.pendientes_pago}
          </span>
        </div>

        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-amber-200/80 dark:border-amber-900/40 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 block mb-1">
            Listos p/ Despachar
          </span>
          <span className="text-2xl font-black text-amber-600 dark:text-amber-400">
            {stats.por_despachar}
          </span>
        </div>

        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-sky-200/80 dark:border-sky-900/40 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wider text-sky-600 dark:text-sky-400 block mb-1">
            En Tránsito / Retiro
          </span>
          <span className="text-2xl font-black text-sky-600 dark:text-sky-400">
            {stats.en_transito}
          </span>
        </div>

        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-emerald-200/80 dark:border-emerald-900/40 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 block mb-1">
            Entregados
          </span>
          <span className="text-2xl font-black text-emerald-600 dark:text-emerald-400">
            {stats.entregados}
          </span>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 border border-slate-200 dark:border-slate-700/60 shadow-sm mb-6 flex flex-col md:flex-row gap-3 items-center justify-between">
        <div className="relative w-full md:w-96">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por cliente, pedido, tracking, producto o destino..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500"
          />
        </div>

        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="todos">Todos los Estados</option>
            <option value="pendiente_transferencia">Pendientes de Pago / Transferencia</option>
            <option value="por_despachar">Pagados / Por Despachar</option>
            <option value="en_transito">En Tránsito</option>
            <option value="listo_retiro">Para Retirar en Local</option>
            <option value="entregado">Entregados</option>
          </select>

          <select
            value={filtroCourier}
            onChange={(e) => setFiltroCourier(e.target.value)}
            className="px-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="todos">Todos los Operadores</option>
            <option value="andreani">Andreani</option>
            <option value="envia">Envia.com (Correo Arg / OCA / Urbano)</option>
            <option value="retiro">Retiro en Óptica</option>
          </select>
        </div>
      </div>

      {/* Tabla de Envíos */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700 text-[11px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50/50 dark:bg-slate-800/50">
                <th className="py-3.5 px-4">Pedido / Productos</th>
                <th className="py-3.5 px-4">Cliente</th>
                <th className="py-3.5 px-4">Operador y Destino</th>
                <th className="py-3.5 px-4">Seguimiento (Tracking)</th>
                <th className="py-3.5 px-4">Estado</th>
                <th className="py-3.5 px-4 text-right">Acciones de Despacho</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
              {enviosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center text-slate-400">
                    No se encontraron envíos web que coincidan con los filtros seleccionados.
                  </td>
                </tr>
              ) : (
                enviosFiltrados.map((envio) => (
                  <tr key={envio.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-750/50 transition-colors">
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-2">
                        <span className="font-black text-slate-900 dark:text-white">
                          {envio.codigo_pedido}
                        </span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                          ${Number(envio.total || 0).toLocaleString('es-AR')}
                        </span>
                      </div>
                      <span className="text-[11px] text-slate-500 dark:text-slate-400 block mt-0.5 font-medium">
                        {envio.items_resumen || 'Armazón óptico'}
                      </span>
                      {envio.fecha && (
                        <span className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                          <CalendarDaysIcon className="w-3 h-3" />
                          {new Date(envio.fecha).toLocaleDateString('es-AR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </span>
                      )}
                    </td>

                    <td className="py-3.5 px-4">
                      <span className="font-semibold text-slate-800 dark:text-slate-200 block">
                        {envio.cliente_nombre}
                      </span>
                      <span className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                        <PhoneIcon className="w-3 h-3" /> {envio.cliente_telefono || 'Sin teléfono'}
                      </span>
                    </td>

                    <td className="py-3.5 px-4 max-w-xs">
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-cyan-700 dark:text-cyan-400 block mb-0.5">
                        {envio.courier}
                        {envio.costo_envio > 0 && (
                          <span className="text-[10px] text-slate-400 font-normal">
                            (${Number(envio.costo_envio).toLocaleString('es-AR')})
                          </span>
                        )}
                      </span>
                      <span className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1" title={envio.direccion}>
                        <MapPinIcon className="w-3 h-3 shrink-0 text-slate-400" />
                        <span className="truncate">{envio.direccion}</span>
                      </span>
                    </td>

                    <td className="py-3.5 px-4">
                      {envio.numero_seguimiento ? (
                        <div className="flex items-center gap-1.5">
                          <code className="text-[11px] bg-slate-100 dark:bg-slate-900 px-2 py-1 rounded font-mono font-bold text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
                            {envio.numero_seguimiento}
                          </code>
                          <button
                            type="button"
                            onClick={() => handleEmitirEtiqueta(envio)}
                            className="text-[10px] text-cyan-600 hover:underline font-bold cursor-pointer"
                            title="Cambiar o regenerar código de seguimiento"
                          >
                            Editar
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          disabled={procesandoId === envio.id}
                          onClick={() => handleEmitirEtiqueta(envio)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-cyan-50 dark:bg-cyan-950/50 text-cyan-700 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800 text-[11px] font-bold hover:bg-cyan-100 transition-colors cursor-pointer"
                        >
                          <QrCodeIcon className="w-3.5 h-3.5" />
                          <span>Generar Etiqueta / Tracking</span>
                        </button>
                      )}
                    </td>

                    <td className="py-3.5 px-4">
                      {getBadgeEstado(envio.estado)}
                    </td>

                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        {(envio.estado === 'pendiente_transferencia' || envio.estado === 'pendiente') && (
                          <button
                            type="button"
                            disabled={procesandoId === envio.id}
                            onClick={() => cambiarEstado(envio, 'pagado')}
                            className="px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-bold shadow-sm transition-colors cursor-pointer"
                            title="Aprobar transferencia bancaria y descontar stock automáticamente"
                          >
                            Acreditar Pago
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => imprimirRotulo(envio)}
                          className="p-1.5 text-slate-600 dark:text-slate-300 hover:text-cyan-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors cursor-pointer"
                          title="Imprimir Rótulo / Etiqueta para pegar en la caja"
                        >
                          <DocumentArrowDownIcon className="w-4 h-4" />
                        </button>

                        <button
                          type="button"
                          onClick={() => notificarWhatsApp(envio)}
                          className="p-1.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-slate-700 rounded-lg transition-colors cursor-pointer"
                          title="Enviar código de seguimiento al cliente por WhatsApp"
                        >
                          <PaperAirplaneIcon className="w-4 h-4" />
                        </button>

                        <select
                          value={envio.estado}
                          disabled={procesandoId === envio.id}
                          onChange={(e) => cambiarEstado(envio, e.target.value)}
                          className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-[11px] font-bold rounded-lg border-none focus:ring-1 focus:ring-cyan-500 cursor-pointer"
                        >
                          <option value="pendiente_transferencia">Verificar Pago</option>
                          <option value="pagado">Pagado (Por despachar)</option>
                          <option value="pendiente_despacho">Por despachar</option>
                          <option value="en_transito">En tránsito</option>
                          <option value="listo_retiro">Para retirar</option>
                          <option value="entregado">Entregado</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Configuración de Logística y Envíos + Instructivo Paso a Paso */}
      <LogisticaConfigModal
        isOpen={modalLogisticaOpen}
        onClose={() => setModalLogisticaOpen(false)}
        initialTab={modalInitialTab}
      />
    </div>
  );
}

GestionEnvios.propTypes = {
  onViewChange: PropTypes.func.isRequired,
  usuario: PropTypes.object
};
